import { Heading } from '@built/design-system';
import * as React from 'react';
import { AuthError, BrandingData, EmailVerificationData } from '../types';
import Footer from './Footer';
import * as styles from './Login.module.scss';
import Auth from '../containers/Auth';
import EmailVerification from '../containers/EmailVerification';
import logo from '../images/blt-logo-web.svg';
import l10n from '@built/i18n';
import Notification from './Notification';
import { BannerStyle } from '@built/design-system/types/Banner/Banner';
const DEFAULT_HEADLINE = () =>
  l10n.t('Login:Constant.headline', 'Powering Smarter Construction Finance');
const DEFAULT_SUBTEXT = () =>
  l10n.t(
    'Login:Constant.subtext',
    'Your platform for faster draws, less friction and complete visibility.'
  );

/**
 * Renders the login page UI. The portion of the page that handles actual
 * authentication is delegated to the Auth component.
 */

// TODO (abe) simplify this and remove tslint ignore below
// tslint:disable-next-line:cyclomatic-complexity
export default function Login(props: LoginProps) {
  const { branding, noteFromReferrer } = props;
  const builtLogo = logo;
  const brandedLogo = (branding && branding.logoFile) || null;
  const brandingStyles = {};
  const brandingElementStyles = {};

  const headline = (branding && branding.headline) || DEFAULT_HEADLINE();
  const subtext = (branding && branding.subtext) || DEFAULT_SUBTEXT();

  const Component = getComponent(props);

  const referrerData = mapReferrerSlug(noteFromReferrer);

  return (
    <div className={styles.loginForm}>
      <div className={styles.left}>
        <div className={styles.loginFormContainer}>
          {brandedLogo ? (
            <img
              className={[styles.logo, styles.logoBranded].join(' ')}
              src={brandedLogo}
              alt="Built Logo"
            />
          ) : (
            <img
              className={[styles.logo].join(' ')}
              src={builtLogo}
              alt="Built Logo"
            />
          )}
          <Component />
          {referrerData && (
            <div className={styles.notesWrapper}>
              <Notification
                type={referrerData.type}
                title={referrerData.title}
                content={referrerData.description}
              />
            </div>
          )}
          <Footer className={styles.copyright} basic={true} />
        </div>
      </div>
      <div className={styles.right}>
        <div
          className={[styles.rightTop, branding && styles.rightTopBranded].join(
            ' '
          )}
        >
          {branding && (
            <div
              style={brandingElementStyles}
              className={[styles.rightTopBrandedElement].join(' ')}
            />
          )}
        </div>
        <div style={brandingStyles} className={styles.rightBottom}>
          <div className={styles.copyContent}>
            <hr
              className={[
                styles.lineDetail,
                branding && styles.lineDetailBranded
              ].join(' ')}
              style={branding && { backgroundColor: branding.primaryColor }}
            />
            <Heading
              size="xxl"
              className={[styles.bltHeading, 'margin-b-xs margin-t-0'].join(
                ' '
              )}
            >
              {headline}
            </Heading>
            <p className={[styles.headingLead, 'margin-t-md'].join(' ')}>
              {subtext}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Determines the main component of the login view that should be
 * rendered.
 *
 * @param props
 */
function getComponent(props: LoginProps) {
  if (props.emailVerificationData) {
    return EmailVerification;
  }
  return Auth;
}

function mapReferrerSlug(slug: string | null = null): ReferrerNoteData | null {
  const referrerSlugMessageMap = {
    already_verified: {
      title: 'Your account has already been verified.',
      description:
        'You have previously verified your account. Please login to continue.',
      type: 'success'
    },
    verified: {
      title: 'Your contact information has been confirmed.',
      description: 'Your account is now active. Login to continue.',
      type: 'success'
    }
  };

  if (slug && referrerSlugMessageMap.hasOwnProperty(slug)) {
    return referrerSlugMessageMap[slug];
  }

  return null;
}
export interface LoginProps {
  /** Any authentication error that should be displayed */
  authError?: AuthError;

  /** Branding data to customize the component's presentation */
  branding?: BrandingData;

  emailVerificationData?: EmailVerificationData;

  languageReady: boolean;

  /** Note slug that will be mapped to display when landing on login page */
  noteFromReferrer?: string;
}

interface ReferrerNoteData {
  title: string;
  description: string;
  type: BannerStyle;
}
